import {ValueRange} from "./ValueRange";
import {stringStartsWith} from "./Utilities";

export enum ObscurePercentToRangeOptionType {
    Off = "Off",
    StandardMode = "StandardMode",
    RockwoolMode = "RockwoolMode" // special mode for Rockwool, a customer
}

//
// Represents a percentage range. Ported from Utilities\PercentRange.cs
//
// Don't edit this file, edit PercentRange.cs and then port changes over to this file.
//
// See PercentRange.cs for comments.
//
export class PercentRange extends ValueRange {
    public constructor(suppliedLowerBound: number, suppliedUpperBound: number) {
        super();

        this.lowerBound = suppliedLowerBound;
        this.upperBound = suppliedUpperBound;
    }

    // ===============================================================================

    public static fromPpm(lowerBoundPpm: number, upperBoundPpm: number) {
        return new PercentRange(
            lowerBoundPpm / (1000.0 / 0.1),
            upperBoundPpm / (1000.0 / 0.1),
        );
    }

    // ===============================================================================

    public static fromSerializedRange(serializedRange: string, cultureName: string): PercentRange {
        const ret = new PercentRange(0, 0);

        ret.extractSerializedRange(serializedRange, cultureName);

        return ret;
    }

    // ===============================================================================

    public clone(): PercentRange {
        return new PercentRange(this.lowerBound, this.upperBound);
    }

    // ===============================================================================

    public round(numDecimalsToRoundTo: number): PercentRange {
        return new PercentRange(
            this.roundValue(this.lowerBound, numDecimalsToRoundTo),
            this.roundValue(this.upperBound, numDecimalsToRoundTo));
    }

    // ===============================================================================

    public percentOf(other: PercentRange): PercentRange {
        return new PercentRange(
            other.lowerBound == 0.0 ? 100.0 : (this.lowerBound / other.lowerBound) * 100.0,
            other.upperBound == 0.0 ? 100.0 : (this.upperBound / other.upperBound) * 100.0
        );
    }

    // ===============================================================================

    public add(other: PercentRange): PercentRange {
        return new PercentRange(
            this.lowerBound + other.lowerBound,
            this.upperBound + other.upperBound
        );
    }

    // ===============================================================================

    public subtract(other: PercentRange): PercentRange {
        const x = this.lowerBound - other.lowerBound;
        const y = this.upperBound - other.upperBound;

        return new PercentRange(
            Math.min(x, y),
            Math.max(x, y)
        );
    }

    // ===============================================================================

    public multiply(other: PercentRange): PercentRange {
        return new PercentRange(
            (this.lowerBound / 100.0) * (other.lowerBound / 100.0) * 100.0,
            (this.upperBound / 100.0) * (other.upperBound / 100.0) * 100.0
        );
    }

    // ===============================================================================

    public divideBy(other: PercentRange): PercentRange {
        const lb =
            (other.lowerBound === 0.0)
                ? 0.0
                : (this.lowerBound / 100.0) / (other.lowerBound / 100.0) * 100.0;

        const ub =
            (other.upperBound === 0.0)
                ? 0.0
                : (this.upperBound / 100.0) / (other.upperBound / 100.0) * 100.0;

        if (lb > ub) {
            return new PercentRange(ub, lb);
        }
        else {
            return new PercentRange(lb, ub);
        }
    }

    // ===============================================================================

    public lowerMinUpperMax(other: PercentRange): PercentRange {
        return new PercentRange(
            Math.min(this.lowerBound, other.lowerBound),
            Math.max(this.upperBound, other.upperBound),
        );
    }

    // ===============================================================================

    public clamp(lowerBound: number, upperBound: number) {
        return new PercentRange(
            Math.max(this.lowerBound, lowerBound),
            Math.min(this.upperBound, upperBound)
        );
    }

    // ===============================================================================

    public toString(cultureName: string, numDecimalsToRoundTo: number): string {
        const lb = this.roundValue(this.lowerBound, numDecimalsToRoundTo);
        const ub = this.roundValue(this.upperBound, numDecimalsToRoundTo);

        const formatOptions: Intl.NumberFormatOptions = {};

        if (numDecimalsToRoundTo !== -1) {
            formatOptions.maximumFractionDigits = numDecimalsToRoundTo;
        }

        if (lb === ub) {
            return ub.toLocaleString(cultureName, formatOptions) + "%";
        }
        else {
            const s0 = lb.toLocaleString(cultureName, formatOptions);

            const s1 = ub.toLocaleString(cultureName, formatOptions);

            if (s0 === s1) {
                return s0 + "%";
            }
            else {
                return s0 + "-" + s1 + "%";
            }
        }
    }

    // ===============================================================================

    /**
     * Returns a string representation of this object, with percentage
     * values converted to "Parts per million" (PPM)
     */
    public toPpmString(cultureName: string, numDecimalsToRoundTo: number): string {
        const lb = this.roundValue((1000.0 / 0.1) * this.lowerBound, numDecimalsToRoundTo);
        const ub = this.roundValue((1000.0 / 0.1) * this.upperBound, numDecimalsToRoundTo);

        const formatOptions: Intl.NumberFormatOptions = {};

        if (numDecimalsToRoundTo !== -1) {
            formatOptions.maximumFractionDigits = numDecimalsToRoundTo;
        }

        if (lb === ub) {
            return ub.toLocaleString(cultureName, formatOptions) + " PPM";
        }
        else {
            const s0 = lb.toLocaleString(cultureName, formatOptions);

            const s1 = ub.toLocaleString(cultureName, formatOptions);

            if (s0 === s1) {
                return s0 + " PPM";
            }
            else {
                return s0 + "-" + s1 + " PPM";
            }
        }
    }

    // ===============================================================================

    public extractSerializedRange(serializedRange: string, cultureName: string): void {
        const defaultToUse = 0.0;

        if (stringStartsWith(serializedRange, "<") || stringStartsWith(serializedRange, "≤")) {
            serializedRange = "0 -" + serializedRange.trimChars([ '<', '≤' ]);
        }
        else if ((stringStartsWith(serializedRange, ">") || stringStartsWith(serializedRange, "≥")) && !this.containsRangeDelimiter(serializedRange)) {
            serializedRange = serializedRange.trimChars([ '>', '≥' ]) + "- 100";
        }

        const splits = this.splitStringToRangedParts(serializedRange);

        const comparisonChars = [ '<', '≤', '>', '≥', '=' ];

        if (splits.length === 0) {
            this.lowerBound = this.upperBound = defaultToUse;
        }
        else if (splits.length === 1) {
            const s = splits[0];

            const k = s.indexOf("<");

            if (k > 0) {
                const subSplits = s.split('<', 2);

                const l = subSplits[0].trim().trimEndString("%").trim();

                const r = subSplits[1]?.trimStartString("=").trim().trimEndString("%").trim() ?? "";

                this.lowerBound = l.toDoubleOrDefault(defaultToUse);
                this.upperBound = r.toDoubleOrDefault(defaultToUse);
            }
            else {
                this.lowerBound = this.upperBound = splits[0].trimEndString("%").trim().trimChars(comparisonChars).trim().toDoubleOrDefault(defaultToUse);
            }
        }
        else if (splits.length >= 2) {
            const d0 = splits[0].trimEndString("%").trim().trimChars(comparisonChars).trim().toDoubleOrDefault(defaultToUse);
            const d1 = splits[1].trimEndString("%").trim().trimChars(comparisonChars).trim().toDoubleOrDefault(defaultToUse);

            this.lowerBound = d0;
            this.upperBound = d1;

            if (this.upperBound < this.lowerBound) {
                this.upperBound = this.lowerBound;
            }
        }
        else {
            this.lowerBound = this.upperBound = defaultToUse;
        }
    }

    // ===============================================================================

    private containsRangeDelimiter(serializedRange: string): boolean {
        for (let i = 0; i < serializedRange.length; ++i) {
            const c = serializedRange[i];
            if ((c !== "-") && (c !== "–") && (c !== "—")) {
                continue;
            }

            const isExponentSign = (c === "-") &&
                (i > 0) &&
                ((serializedRange[i - 1] === "e") || (serializedRange[i - 1] === "E")) &&
                (i + 1 < serializedRange.length) &&
                /\d/.test(serializedRange[i + 1]);

            if (!isExponentSign) {
                return true;
            }
        }

        return false;
    }
}
